import { CircularProgress } from '@material-ui/core';
import { Table, Tag, Tooltip } from 'antd';
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import './ProgrammaticAnalyticReport.css';

// `hour` is "YYYY-MM-DD-HH" in SGT and is already local — split it, never
// re-parse it as a timestamp.
const hourLabel = (row) => `${row.date} ${String(row.hourOfDay).padStart(2, '0')}:00`;

const percentText = (value) =>
  value === null || value === undefined ? '—' : `${value}%`;

// Discrepancy tolerance is 1%, so anything under 99% is worth flagging.
const percentColor = (value) => {
  if (value === null || value === undefined) return 'default';
  if (value >= 99) return 'green';
  if (value >= 90) return 'orange';
  return 'red';
};

const ProgrammaticAnalyticReport = ({ loading }) => {
  const { programmatic } = useSelector(
    (state) => ({ programmatic: state.analytic.programmatic }),
    shallowEqual
  );

  const table = programmatic?.table || [];
  const totals = programmatic?.totals;

  const columns = [
    {
      title: 'Hour',
      dataIndex: 'hour',
      render: (_text, row) => (
        <div className="table-name">
          {hourLabel(row)}
          {row.stillPending > 0 && (
            <Tooltip title="Inside the vendor's 30-minute confirmation window — these numbers can still change.">
              <span className="programmatic-pending-marker">*</span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Requested',
      dataIndex: 'requested',
      sorter: (a, b) => a.requested - b.requested,
    },
    {
      title: 'Played',
      dataIndex: 'played',
      sorter: (a, b) => a.played - b.played,
    },
    {
      title: 'Confirmed',
      dataIndex: 'confirmed',
      sorter: (a, b) => a.confirmed - b.confirmed,
    },
    {
      title: 'Confirm Rate',
      dataIndex: 'confirmPercent',
      // Sort on the unrounded float, display the rounded one.
      sorter: (a, b) => a.confirmRate - b.confirmRate,
      render: (value) => (
        <Tag color={percentColor(value)}>{percentText(value)}</Tag>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="analytic-chart">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="analytic-chart programmatic-report">
      {totals && (
        <div className="programmatic-summary">
          <div className="programmatic-summary-card">
            <span>Requested</span>
            <strong>{totals.requested}</strong>
          </div>
          <div className="programmatic-summary-card">
            <span>Played</span>
            <strong>{totals.played}</strong>
          </div>
          <div className="programmatic-summary-card">
            <span>Confirmed</span>
            <strong>{totals.confirmed}</strong>
          </div>
          <div className="programmatic-summary-card">
            <span>Confirm rate</span>
            <strong className={`rate-${percentColor(totals.confirmPercent)}`}>
              {percentText(totals.confirmPercent)}
            </strong>
          </div>
        </div>
      )}

      <Table
        rowKey="hour"
        columns={columns}
        dataSource={table}
        pagination={{ pageSize: 24 }}
        // Only hours with activity are returned, so gaps in the list are
        // screens that were off, not missing data.
        rowClassName={(row) =>
          row.stillPending > 0 ? 'programmatic-row-pending' : ''
        }
        summary={() =>
          totals ? (
            <Table.Summary.Row>
              <Table.Summary.Cell>Total</Table.Summary.Cell>
              <Table.Summary.Cell>{totals.requested}</Table.Summary.Cell>
              <Table.Summary.Cell>{totals.played}</Table.Summary.Cell>
              <Table.Summary.Cell>{totals.confirmed}</Table.Summary.Cell>
              <Table.Summary.Cell>
                <Tag color={percentColor(totals.confirmPercent)}>
                  {percentText(totals.confirmPercent)}
                </Tag>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          ) : null
        }
      />

      <p className="programmatic-footnote">
        * Hour still inside the vendor&apos;s 30-minute confirmation window —
        its confirm rate can still move. Hours with no activity are omitted.
      </p>
    </div>
  );
};

export default ProgrammaticAnalyticReport;
